// Dashboard: die Zahlen, die man morgens sehen will, und Wege dorthin.
// Farbe ist hier kein Schmuck — jede Kachel trägt die Farbe ihrer Bedeutung,
// damit Probleme (rot/gelb) sofort ins Auge fallen.
import { Link } from 'react-router-dom';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useKennzahlen } from '../lib/kennzahlen';
import { euro, datum } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Abzeichen, Karte, KartenKopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import TafelKurz from '../../aufgaben/komponenten/TafelKurz';

type Ton = 'marke' | 'gruen' | 'gelb' | 'rot' | 'violett' | 'tuerkis';

const TOENE: Record<Ton, { karte: string; wert: string; symbol: string }> = {
  marke:   { karte: 'from-indigo-500/10 to-indigo-500/0  border-indigo-200  dark:border-indigo-900/60',  wert: 'text-indigo-700 dark:text-indigo-300',   symbol: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400' },
  gruen:   { karte: 'from-emerald-500/10 to-emerald-500/0 border-emerald-200 dark:border-emerald-900/60', wert: 'text-emerald-700 dark:text-emerald-300', symbol: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  gelb:    { karte: 'from-amber-500/10 to-amber-500/0   border-amber-200   dark:border-amber-900/60',   wert: 'text-amber-700 dark:text-amber-300',     symbol: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  rot:     { karte: 'from-rose-500/10 to-rose-500/0     border-rose-200    dark:border-rose-900/60',    wert: 'text-rose-700 dark:text-rose-300',       symbol: 'bg-rose-500/15 text-rose-600 dark:text-rose-400' },
  violett: { karte: 'from-violet-500/10 to-violet-500/0 border-violet-200  dark:border-violet-900/60',  wert: 'text-violet-700 dark:text-violet-300',   symbol: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  tuerkis: { karte: 'from-cyan-500/10 to-cyan-500/0     border-cyan-200    dark:border-cyan-900/60',    wert: 'text-cyan-700 dark:text-cyan-300',       symbol: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400' },
};

function Kachel({ titel, wert, zusatz, ton, symbol, ziel }: {
  titel: string; wert: string; zusatz?: string; ton: Ton; symbol: string; ziel?: string;
}) {
  const s = TOENE[ton];
  const inhalt = (
    <div className={`h-full rounded-xl border bg-gradient-to-br p-4 transition ${s.karte} ${ziel ? 'hover:shadow-md' : ''} bg-white dark:bg-slate-900`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">{titel}</p>
        <span aria-hidden className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg text-sm ${s.symbol}`}>{symbol}</span>
      </div>
      <p className={`mt-2 text-2xl font-bold ${s.wert}`}>{wert}</p>
      {zusatz && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{zusatz}</p>}
    </div>
  );
  return ziel ? <Link to={ziel} className="block h-full">{inhalt}</Link> : inhalt;
}

function Schnellaktion({ ziel, symbol, text, ton }: { ziel: string; symbol: string; text: string; ton: Ton }) {
  const s = TOENE[ton];
  return (
    <Link
      to={ziel}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 transition hover:border-slate-300 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-700"
    >
      <span aria-hidden className={`grid h-8 w-8 place-items-center rounded-lg ${s.symbol}`}>{symbol}</span>
      {text}
    </Link>
  );
}

export default function Uebersicht() {
  const { benutzer, mandant, flags, istAdmin } = useAuth();
  const { t } = useSprache();

  // Die Datenbeschaffung liegt in lib/kennzahlen.ts, weil das Kachelbrett der
  // App dieselben Zahlen braucht. An der Anzeige hier ändert das nichts.
  const { aufgaben, team, artikel, rechnungen, buch, laedt } = useKennzahlen(istAdmin);

  if (laedt) return <div className="flex justify-center py-16"><Spinner /></div>;

  const heute = new Date().toDateString();
  const umsatzHeute = rechnungen
    .filter(r => r.status !== 'entwurf' && r.status !== 'storniert' && new Date(r.datum).toDateString() === heute)
    .reduce((s, r) => s + r.brutto, 0);
  const belegeHeute = rechnungen.filter(r =>
    r.status !== 'entwurf' && r.status !== 'storniert' && new Date(r.datum).toDateString() === heute).length;

  const offeneRechnungen = rechnungen.filter(r => r.status === 'gestellt');
  const ueberfaellig = offeneRechnungen.filter(r => r.faelligAm && new Date(r.faelligAm) < new Date());
  const knapp = artikel.filter(p => p.aktiv !== false && (p.bestand ?? 0) <= (p.mindestbestand ?? 0));
  const lagerwert = artikel.reduce((s, p) => s + (p.bestand || 0) * (p.einkaufspreis || 0), 0);
  const offeneAufgaben = aufgaben.filter(a => a.column !== 'done');
  const online = team.filter(x => x.online);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {t('uebersicht.begruessung')}, {benutzer?.name?.split(' ')[0]}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {mandant?.name} · {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Geld zuerst — das ist die Zahl, für die man das Dashboard öffnet. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kachel titel={t('uebersicht.umsatzHeute')} wert={euro(umsatzHeute)} ton="gruen" symbol="€" ziel="/rechnungen"
                zusatz={`${belegeHeute} ${belegeHeute === 1 ? 'Beleg' : 'Belege'}`} />
        <Kachel titel={t('uebersicht.offeneForderungen')} wert={euro(offeneRechnungen.reduce((s, r) => s + r.brutto, 0))}
                ton={ueberfaellig.length > 0 ? 'rot' : 'marke'} symbol="⎗" ziel="/rechnungen?status=gestellt"
                zusatz={ueberfaellig.length > 0
                  ? `${ueberfaellig.length} überfällig · ${euro(ueberfaellig.reduce((s, r) => s + r.brutto, 0))}`
                  : `${offeneRechnungen.length} ${offeneRechnungen.length === 1 ? 'Rechnung' : 'Rechnungen'}`} />
        <Kachel titel={t('uebersicht.lagerwert')} wert={euro(lagerwert)} ton="violett" symbol="▣" ziel="/lager"
                zusatz={`${artikel.length} Artikel`} />
        <Kachel titel={t('uebersicht.unterMindest')} wert={String(knapp.length)}
                ton={knapp.length > 0 ? 'gelb' : 'tuerkis'} symbol="⚠" ziel="/lager?knapp=1"
                zusatz={knapp.length > 0 ? 'nachbestellen' : 'alles ausreichend'} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kachel titel={t('uebersicht.offeneAufgaben')} wert={String(offeneAufgaben.length)} ton="marke" symbol="☑" ziel="/aufgaben"
                zusatz={`${aufgaben.length} ${t('uebersicht.insgesamt')}`} />
        <Kachel titel={t('uebersicht.laufendeTimer')} wert={String(aufgaben.filter(a => a.timerRunning).length)}
                ton="tuerkis" symbol="◷" ziel="/aufgaben" />
        <Kachel titel={t('uebersicht.teamOnline')} wert={String(online.length)} ton="gruen" symbol="☺" ziel="/team"
                zusatz={`${t('uebersicht.von')} ${team.length}`} />
        <Kachel
          titel={t('uebersicht.benutzerImTarif')}
          wert={flags ? (flags.maxUsers < 0 ? t('uebersicht.unbegrenzt') : `${team.length} / ${flags.maxUsers}`) : '—'}
          ton="violett" symbol="◈" ziel={istAdmin ? '/tarif' : undefined}
        />
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">{t('uebersicht.schnell')}</h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Schnellaktion ziel="/kasse" symbol="▤" text={t('uebersicht.kassieren')} ton="gruen" />
          <Schnellaktion ziel="/rechnungen" symbol="⎗" text={t('rechnungen.schreiben')} ton="marke" />
          <Schnellaktion ziel="/lager" symbol="▣" text={t('uebersicht.wareneingang')} ton="violett" />
          <Schnellaktion ziel="/kunden" symbol="☻" text={t('kunden.neu')} ton="tuerkis" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {knapp.length > 0 && (
          <Karte>
            <KartenKopf titel={t('uebersicht.nachbestellen')} hinweis={`${knapp.length} Artikel unter Mindestbestand`}
                        aktion={<Link to="/lager?knapp=1" className="text-sm font-medium text-marke-600 hover:underline dark:text-marke-400">{t('uebersicht.alleAnsehen')}</Link>} />
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {knapp.slice(0, 5).map(p => (
                <li key={p.id} className="flex items-center gap-3 px-5 py-2.5">
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-900 dark:text-slate-100">{p.name}</span>
                  <Abzeichen art="gelb">{p.bestand ?? 0} / {p.mindestbestand} {p.einheit}</Abzeichen>
                </li>
              ))}
            </ul>
          </Karte>
        )}

        {ueberfaellig.length > 0 && (
          <Karte>
            <KartenKopf titel={t('uebersicht.ueberfaellig')} hinweis={t('uebersicht.zahlungsziel')}
                        aktion={<Link to="/rechnungen" className="text-sm font-medium text-marke-600 hover:underline dark:text-marke-400">{t('uebersicht.alleAnsehen')}</Link>} />
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {ueberfaellig.slice(0, 5).map(r => (
                <li key={r.id} className="flex items-center gap-3 px-5 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-900 dark:text-slate-100">{r.kundeName}</p>
                    <p className="text-xs text-rose-600 dark:text-rose-400">fällig war {datum(r.faelligAm)}</p>
                  </div>
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{euro(r.brutto)}</span>
                </li>
              ))}
            </ul>
          </Karte>
        )}


        {buch && buch.monate.length > 0 && (
          <Karte>
            <KartenKopf titel={t('uebersicht.umsatzMonat')} hinweis={`Rohertrag ${euro(buch.rohertrag)}`}
                        aktion={istAdmin ? <Link to="/buchhaltung" className="text-sm font-medium text-marke-600 hover:underline dark:text-marke-400">{t('uebersicht.alleAnsehen')}</Link> : undefined} />
            <ul className="space-y-2 p-5">
              {buch.monate.slice(-6).map(m => {
                const hoch = Math.max(1, ...buch.monate.slice(-6).map(x => x.brutto));
                return (
                  <li key={m.monat}>
                    <div className="mb-1 flex justify-between text-xs">
                      <span className="text-slate-600 dark:text-slate-400">
                        {new Date(m.monat + '-01').toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })}
                      </span>
                      <span className="text-slate-900 dark:text-slate-100">{euro(m.brutto)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                           style={{ width: `${Math.max(3, (m.brutto / hoch) * 100)}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Karte>
        )}
      </div>

      {/* Die Tafel für heute — ganz unten, die Woche steht auf der Tafel selbst. */}
      <TafelKurz />
    </div>
  );
}
